import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast, type ToastContentProps } from 'react-toastify';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { cls } from '@/utils/cls';

import { DuplicateTitleDialog } from './duplicate-title-dialog';

export const taskCardCreateForm = z.object({
  title: z.string().min(3),
});

export type TaskCardCreateForm = z.infer<typeof taskCardCreateForm>;

export type TaskCardCreateProps = {
  className?: string;
  onCreate: (data: TaskCardCreateForm) => Promise<void>;
};

const TaskCardCreate = forwardRef<HTMLFormElement, TaskCardCreateProps>(
  function ({ className, onCreate }, ref) {
    const form = useForm<TaskCardCreateForm>({
      resolver: zodResolver(taskCardCreateForm),
    });

    const [duplicateTitleProps, setDuplicateTitleProps] = useState<{
      title: string;
    } | null>(null);

    return (
      <Form {...form}>
        <form
          ref={ref}
          onSubmit={form.handleSubmit(async function (data) {
            try {
              await toast.promise(onCreate(data), {
                pending: 'Creating the task',
                success: 'Task created',
                error: {
                  render: function ({
                    data: { status },
                  }: ToastContentProps<any>) {
                    if (status === 422) {
                      setDuplicateTitleProps({ title: data.title });
                    }

                    return 'Failed to create the task';
                  },
                },
              });
            } catch (_) {
              // Error is handled by the toast
            }
            form.reset();
            form.setFocus('title');
          })}
          className={cls(
            'flex flex-col gap-4 rounded border-2 border-slate-200 bg-white shadow-md',
            className,
          )}
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <input
                    {...field}
                    placeholder="Your title"
                    className="border-none p-4 outline-none focus:border-none focus:ring-0"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
        <DuplicateTitleDialog
          open={Boolean(duplicateTitleProps)}
          firstTitle={duplicateTitleProps?.title ?? ''}
          onCreate={onCreate}
          onClose={() => setDuplicateTitleProps(null)}
        />
      </Form>
    );
  },
);

TaskCardCreate.displayName = 'TaskCardCreate';
export { TaskCardCreate };
